

<template>
  <div class="grading-panel">
    <div class="content-wrapper">
      <div class="top-bar">
        <button class="back-btn">
          <span class="arrow-icon"></span>
        </button>
        <h2>Cut Plane Settings</h2>
        <v-icon @click="showCutFillHelp" class="help-icon" dark color="white"
          >mdi-help-circle-outline</v-icon
        >
      </div>
      <!-- Grade Type -->
      <div class="control-group mt-1" v-show="!hideType">
        <label>Slope Type </label>
        <div class="button-group">
          <button
            v-for="type in gradeTypes"
            :key="type.value"
            :class="{ active: gradeType === type.value }"
            @click="gradeType = type.value"
          >
            {{ type.label }}
          </button>
        </div>
      </div>

      <!-- Grade Value -->
      <div class="control-group">
        <label v-if="hideType">{{ gradeType | firstToUpper }} Value</label>
        <label v-else>Value</label>
        <!-- Rise/Run with two separate inputs -->
        <div v-if="gradeType === 'ratio'" class="ratio-inputs">
          <input
            type="text"
            v-model="riseValue"
            inputmode="numeric"
            pattern="[0-9]*"
            @focus="handleFocus"
            @blur="handleBlur"
            placeholder="Rise"
          />
          <span class="ratio-separator">:</span>
          <input
            type="text"
            v-model="runValue"
            inputmode="numeric"
            pattern="[0-9]*"
            @focus="handleFocus"
            @blur="handleBlur"
            placeholder="Run"
          />
        </div>

        <div v-else class="input-with-unit">
          <input
            type="number"
            v-model.number="gradeValue"
            inputmode="numeric"
            pattern="[0-9]*"
            @focus="handleFocus"
            @blur="handleBlur"
            :step="gradeStep"
          />
          <span class="unit">{{ unitLabel }}</span>
        </div>
        <p v-if="gradeType === 'ratio' && ratioError" class="error">
          {{ ratioError }}
        </p>
      </div>

      <!-- Direction -->
      <div class="control-group" v-show="!hideType">
        <label>Direction</label>
        <div class="direction-control">
          <input type="range" v-model.number="direction" min="0" max="359" />
          <!-- Reset Button -->

          <div class="direction-value">
            {{ direction }}°
            <button @click="direction = 0" class="reset-btn">
              <v-icon color="white">mdi-restore</v-icon>
            </button>
          </div>
        </div>
      </div>

      <!-- Preview Panel with Plane -->
      <div class="preview-panel">
        <div class="preview-container">
          <div
            class="cut-plane"
            :style="{
              transform: `rotate(${direction}deg) rotateX(${planeRotationAngle}deg)`,
              opacity: 0.8,
            }"
          ></div>
        </div>
      </div>
    </div>
    <!-- Actions -->
    <div class="action-buttons">
      <button @click="reset" class="reset-btn">Reset</button>

      <button @click="applyGrade" class="apply-btn">Apply</button>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2/dist/sweetalert2.js';
import '../swal.css';

export default {
  name: 'GradingApp',
  data() {
    return {
      hideType: false,
      gradeType: 'percentage',
      gradeValue: 0,
      riseValue: '1',
      runValue: '1',
      ratioError: '',
      direction: 0,
      gradeTypes: [
        { value: 'percentage', label: 'Percentage' },
        { value: 'ratio', label: 'Rise/Run' },
        { value: 'angle', label: 'Angle' },
      ],
    };
  },
  filters: {
    firstToUpper(value) {
      if (!value) return '';
      value = value.toString();
      value = value == 'ratio' ? 'Rise/Run' : value;
      return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
    },
  },
  mounted() {
    LiveChatWidget.call('hide');
  },
  computed: {
    gradeStep() {
      const steps = {
        percentage: 0.1,
        ratio: 0.01,
        angle: 1,
      };
      return steps[this.gradeType];
    },
    unitLabel() {
      const units = {
        percentage: '%',
        ratio: ':1',
        angle: '°',
      };
      return units[this.gradeType];
    },
    ratioInput: {
      get() {
        return `${this.riseValue}:${this.runValue}`;
      },
      set(value) {
        const parts = value.split(':');
        if (parts.length === 2) {
          this.riseValue = parts[0];
          this.runValue = parts[1];
        }
      },
    },
    planeRotationAngle() {
      try {
        switch (this.gradeType) {
          case 'percentage':
            return Math.atan(this.gradeValue / 100) * (180 / Math.PI);
          case 'ratio':
            const { rise, run } = this.parseRatio(this.ratioInput);
            return Math.atan(rise / run) * (180 / Math.PI);
          case 'angle':
            return this.gradeValue;
          default:
            return 0;
        }
      } catch (e) {
        return 0;
      }
    },
  },
  methods: {
    reset() {
      this.gradeValue = 0;
      this.riseValue = '1';
      this.runValue = '1';
      this.direction = 0;
      this.ratioError = '';
    },

    // Update parseRatio to use the new values
    parseRatio() {
      const rise = parseFloat(this.riseValue);
      const run = parseFloat(this.runValue);

      if (!this.riseValue || !this.runValue) {
        this.ratioError = 'Both values are required';
        return { rise: 1, run: 1 };
      }

      if (isNaN(rise) || isNaN(run)) {
        this.ratioError = 'Invalid numbers';
        return { rise: 1, run: 1 };
      }

      if (run === 0) {
        this.ratioError = 'Run cannot be zero';
        return { rise: 1, run: 1 };
      }

      this.ratioError = '';
      return { rise, run };
    },
    applyGrade() {
      console.log('Applied:', {
        type: this.gradeType,
        value: this.gradeValue,
        ratio: this.ratioInput,
        direction: this.direction,
        angle: this.planeRotationAngle,
      });
    },
    reset() {
      this.gradeValue = 0;
      this.ratioInput = '1:1';
      this.direction = 0;
      this.ratioError = '';
    },
    showCutFillHelp() {
      Swal.fire({
        title: 'Cut Plane Settings',
        text: 'Volume is calculated by projecting to a horizontal (flat) plane. You can change the projection to a sloped plane by entering a percentage, rise/run ratio or angle. You can also change the direction of the slope.',
        width: 600,
        padding: '3em',
        color: 'black',
        confirmButtonColor: 'black',
        borderRadius: '8',
        customClass: {
          popup: 'dark-swal',
          confirmButton: 'swal-confirm-btn',
        },
        backdrop: `rgba(20, 18, 24, 0.8)`,
      });
    },
    handleFocus(event) {
      this.activeInput = event.target;
      this.hideType = true;

      setTimeout(() => {
        event.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    },

    handleBlur(event) {
      this.hideType = false;
      this.activeInput = null;
    },
  },
  watch: {
    watch: {
      gradeType(newType) {
        if (newType === 'ratio' && (!this.ratioInput || this.ratioError)) {
          this.riseValue = '1';
          this.runValue = '1';
          this.ratioError = '';
        } else if (newType !== 'ratio') {
          this.ratioError = '';
        }

        const currentAngle = this.planeRotationAngle;
        switch (newType) {
          case 'percentage':
            this.gradeValue = Math.tan(currentAngle * (Math.PI / 180)) * 100;
            break;
          case 'ratio':
            if (this.gradeValue > 0) {
              const runValue = Math.round(
                1 / Math.tan(currentAngle * (Math.PI / 180))
              );
              this.riseValue = '1';
              this.runValue = runValue.toString();
            } else {
              this.riseValue = '1';
              this.runValue = '1';
            }
            break;
          case 'angle':
            this.gradeValue = currentAngle;
            break;
        }
      },
    },
  },
};
</script>

<style>
.reset-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
  padding: 16px;
  background: #1f1f1f;
  border-radius: 16px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
}

.reset-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #2c2c2c;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 14px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
  text-align: center;
}

.reset-btn:hover {
  background: #0dd7b5;
  color: #ffffff;
  transform: scale(1.05);
}

.reset-icon {
  display: inline-block;
  width: 16px;
  height: 16px;
  margin-right: 8px;
  border: solid #0dd7b5;
  border-width: 2px 2px 0 0;
  transform: rotate(45deg);
  transition: transform 0.2s ease;
}

.reset-btn:hover .reset-icon {
  transform: rotate(405deg);
}

.reset-btn:hover {
  background: #0dd7b5;
  color: #ffffff;
  transform: scale(1.05);
}

/* Reset Icon */
.reset-icon {
  font-size: 1.5rem;
  margin-right: 8px;
  color: #0dd7b5;
  transition: transform 0.2s ease;
}

.reset-btn:hover .reset-icon {
  transform: rotate(360deg);
}

.error {
  background-color: #cf6679;
  font-size: 0.75rem;
  color: #ffffff;
  font-weight: 500;
  padding: 12px;
  margin-top: 8px;
}

.content-wrapper {
  flex: 1;
  overflow-y: auto;
  padding-bottom: 80px;
}

.grading-panel {
  width: 100%;
  min-height: 100vh;
  background: #141218;
  color: white;
  display: flex;
  flex-direction: column;
}

.top-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: #121212;
  color: #ffffff;
  border-bottom: 1px solid #1f1f1f;
}

.top-bar h2 {
  font-size: 1.25rem;
  font-weight: 600;
  text-align: center;
  margin: 0;
  flex-grow: 1;
  color: #ffffff;
}

.back-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: white;
  transition: background 0.2s ease;
}

.back-btn:hover {
  background: #1f1f1f;
}

.arrow-icon {
  display: inline-block;
  width: 12px;
  height: 12px;
  border: solid white;
  border-width: 2px 2px 0 0;
  transform: rotate(-135deg);
  margin-left: 4px;
}

.help-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: transparent;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #34d399;
  transition: background 0.2s ease;
}

.help-btn:hover {
  background: #1f1f1f;
}

.help-icon {
  font-size: 1.5rem;
  font-weight: 700;
  color: #34d399;
  cursor: pointer;
}

.control-group {
  padding: 1rem;
  border-bottom: 1px solid #211f26;
}

.control-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.button-group {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.25rem;
  background: #211f26;
  padding: 0.25rem;
  border-radius: 0.5rem;
}

.button-group button {
  padding: 0.5rem;
  border: none;
  background: transparent;
  color: white;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
}

.button-group button.active {
  background: #0dd7b5;
  color: #000000;
}

.input-with-unit {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.input-with-unit input {
  flex: 1;
  background: #211f26;
  border: 1px solid #374151;
  padding: 0.5rem;
  color: white;
  border-radius: 0.375rem;
  text-align: right;
}

.unit {
  width: 2rem;
  color: #9ca3af;
}

.direction-control {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.direction-control input[type='range'] {
  flex: 1;
  height: 0.5rem;
  background: #211f26;
  border-radius: 0.25rem;
  -webkit-appearance: none;
}

.direction-control input[type='range']::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 1rem;
  height: 1rem;
  background: #0dd7b5;
  border-radius: 50%;
  cursor: pointer;
}

/* Slider Track */
.direction-control input[type='range'] {
  width: 100%;
  -webkit-appearance: none;
  height: 8px;
  background: #1f1f1f; /* Track color */
  border-radius: 8px;
  outline: none;
  position: relative;
}

/* Thumb (Slider Handle) */
.direction-control input[type='range']::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 16px; /* Thumb size */
  height: 16px;
  background: #0dd7b5;
  border-radius: 50%; /* Rounded thumb */
  cursor: pointer;
  position: relative;
  top: -4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Active Thumb Focus Effect */
.direction-control input[type='range']:focus::-webkit-slider-thumb {
  box-shadow: 0 0 8px #0dd7b5;
}

/* Track for Firefox */
.direction-control input[type='range']::-moz-range-track {
  height: 8px;
  background: #1f1f1f;
  border-radius: 8px;
}

/* Thumb for Firefox */
.direction-control input[type='range']::-moz-range-thumb {
  width: 16px;
  height: 16px;
  background: #0dd7b5;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Track for Edge/IE */
.direction-control input[type='range']::-ms-track {
  height: 8px;
  background: transparent;
  border-color: transparent;
  color: transparent;
}

.direction-control input[type='range']::-ms-fill-lower {
  background: #1f1f1f;
}

.direction-control input[type='range']::-ms-fill-upper {
  background: #1f1f1f;
}

.direction-control input[type='range']::-ms-thumb {
  width: 16px;
  height: 16px;
  background: #0dd7b5;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  margin-top: -4px;
}

.direction-value {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
}

.preview-panel {
  position: relative;
  height: 300px;
  background: #211f26;
  margin: 1rem;
  border-radius: 0.5rem;
  perspective: 800px;
  overflow: hidden;
}

.preview-container {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 1000px;
}

.cut-plane {
  width: 200px;
  height: 200px;
  background: #2c2c2c;
  transform-style: preserve-3d;
  transform-origin: center center;
  transition: transform 0.2s ease;
  border: 1px solid #444;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  padding: 1rem;
}

.action-buttons button {
  flex: 1;
  padding: 0.5rem;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

.action-buttons {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  gap: 0.5rem;
  padding: 1rem;
  background: #141218;
  border-top: 1px solid #211f26;
  z-index: 100;
  min-height: 70px;
  box-shadow: 0 -4px 6px rgba(0, 0, 0, 0.1);
}

.action-buttons button {
  flex: 1;
  padding: 0.75rem;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.2s;
  font-size: 1rem;
  min-height: 48px;
}

.apply-btn {
  background: #0dd7b5;
  color: black;
}

.apply-btn:hover {
  background: #047857;
}

.reset-btn {
  background: #374151;
  color: white;
}

.reset-btn:hover {
  background: #4b5563;
}

input[type='range'] {
  background: #333;
}

input[type='range']::-webkit-slider-runnable-track {
  background: #333;
  height: 4px;
  border-radius: 2px;
}

.ratio-inputs {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ratio-inputs input {
  flex: 1;
  background: #211f26;
  border: 1px solid #374151;
  padding: 0.5rem;
  color: white;
  border-radius: 0.375rem;
  text-align: right;
  width: 100%;
}

.ratio-separator {
  font-size: 1.25rem;
  color: #9ca3af;
  padding: 0 0.25rem;
}

.dark-swal {
  background: #141218 !important;
  color: #ffffff !important;
  border: 1px solid #211f26 !important;
}

.swal-confirm-btn {
  background: #0dd7b5 !important;
  color: black !important;
}

.swal2-title {
  color: #ffffff !important;
}

.swal2-html-container {
  color: #9ca3af !important;
}
</style>



